import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  CircularProgress 
} from '@mui/material';
import comms from './comms';

function VehicleSelection({ onVehicleSelected, onBack, onChangeFleetCode }) {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVehicleAlreadySelected, setIsVehicleAlreadySelected] = useState(false);
  const [savedVehicle, setSavedVehicle] = useState(null);

  useEffect(() => {
    fetchVehicles();
    
    // Check if vehicle is already saved and confirmed
    const savedVehicleData = localStorage.getItem('selectedVehicle');
    const savedVehicleId = localStorage.getItem('selectedVehicleId');
    
    if (savedVehicleData && savedVehicleId) {
      try {
        const vehicleData = JSON.parse(savedVehicleData);
        setSavedVehicle(vehicleData);
        setSelectedVehicle(savedVehicleId);
        setIsVehicleAlreadySelected(true);
      } catch (error) {
        console.error('Error parsing saved vehicle:', error);
        // Clear corrupted data
        localStorage.removeItem('selectedVehicle');
        localStorage.removeItem('selectedVehicleId');
      }
    }
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const vehicleList = await comms.getAvailableVehicles();
      setVehicles(vehicleList);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      setError('Failed to load vehicles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicle(vehicleId);
  };

  const handleSave = () => {
    if (selectedVehicle) {
      const vehicle = vehicles.find(v => v.Id === selectedVehicle);
      localStorage.setItem('selectedVehicleId', selectedVehicle);
      localStorage.setItem('selectedVehicle', JSON.stringify(vehicle));
      onVehicleSelected(vehicle);
    }
  };

  const handleChangeVehicle = () => {
    // Clear saved vehicle data to allow reselection
    localStorage.removeItem('selectedVehicle');
    localStorage.removeItem('selectedVehicleId');
    setIsVehicleAlreadySelected(false);
    setSavedVehicle(null);
    setSelectedVehicle('');
  };

  const handleConfirmSavedVehicle = () => {
    if (savedVehicle) {
      onVehicleSelected(savedVehicle);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading vehicles...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      height="100vh"
      sx={{ padding: 2 }}
    >
      <Paper elevation={3} sx={{ padding: 4, minWidth: 400 }}>
        <Typography variant="h5" align="center" gutterBottom>
          {isVehicleAlreadySelected ? 'Vehicle Already Selected' : 'Select Vehicle'}
        </Typography>
        
        {error && (
          <Typography color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {isVehicleAlreadySelected ? (
          // Show already selected vehicle
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              Selected Vehicle:
            </Typography>
            <Paper elevation={1} sx={{ padding: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="body1" align="center">
                <strong>{savedVehicle?.Name || savedVehicle?.Code || 'Unknown Vehicle'}</strong>
              </Typography>
              {savedVehicle?.Tag && (
                <Typography variant="body2" align="center" color="textSecondary">
                  Tag: {savedVehicle.Tag}
                </Typography>
              )}
            </Paper>
            <Typography variant="body2" align="center" sx={{ mt: 1, color: 'text.secondary' }}>
              Vehicle selection is locked. You can proceed with login.
            </Typography>
          </Box>
        ) : (
          // Show vehicle selection dropdown
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Vehicle</InputLabel>
            <Select
              value={selectedVehicle}
              onChange={(e) => handleVehicleSelect(e.target.value)}
              label="Vehicle"
            >
              {vehicles.map((vehicle) => (
                <MenuItem key={vehicle.Id} value={vehicle.Id}>
                  {vehicle.Name || vehicle.Code} 
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={onChangeFleetCode}
          >
            Change Fleet Code
          </Button>
          {isVehicleAlreadySelected ? (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleChangeVehicle}
                size="small"
              >
                Change Vehicle
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmSavedVehicle}
              >
                Continue with Vehicle
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!selectedVehicle}
            >
              Save
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default VehicleSelection;
